import React, { useEffect, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography, Chip,
} from "@mui/material";
import { Cancel, RestartAlt } from "@mui/icons-material";

import { HelpBox, VariableChip } from "@/components/atoms";
import { useAudienceActions, useBuilder, useBuilderActions } from "@/context";
import { Variable, VariableGroup } from "@/types";
import {
  operatorOptions,
  demographicsCheck,
  getVariableIcon,
} from "@/utils";

interface SelectionCardProps {
  varGroup: VariableGroup;
  numQueryGroups: number;
}

const SelectionCard: React.FC<SelectionCardProps> = ({ varGroup, numQueryGroups }) => {
  const audienceActions = useAudienceActions();
  const { activeGroup, disabled } = useBuilder();
  const builder = useBuilderActions();
  const active = activeGroup === varGroup.id;

  const allSameDemog = demographicsCheck(varGroup.variables);

  const blockClicks = (event: React.MouseEvent) => {
    event.stopPropagation();
  }

  useEffect(() => {
    if (demographicsCheck(varGroup.variables) && varGroup.operator === 'AND') {
      audienceActions.setGroupOperator(varGroup.id, 'OR');
    }
  }, [varGroup.variables, varGroup.operator, varGroup.id, audienceActions]);

  const handleRemoveGroup = () => builder.removeGroup(varGroup.id);

  const handleChangeOperator = (operator: string) => {
    audienceActions.setGroupOperator(varGroup.id, operator);
  };

  const handleClear = () => {
    audienceActions.resetGroup(varGroup.id);
  };

  const handleVariableDelete = (variable: Variable) => {
    audienceActions.removeVariable(varGroup.id, variable.tag_id);
  };

  return (
    <Card
      onClick={disabled ? undefined : () => builder.toggleGroup(varGroup.id)}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: 360,
        flexShrink: 0,
        height: "auto",
        border: active ? 1 : 0,
        borderColor: active ? "secondary.main" : undefined,
        cursor: disabled ? undefined : "pointer",
        "&:hover": {
          backgroundImage: disabled ? undefined : "linear-gradient(rgba(255,255,255,0.08), rgba(255,255,255,0.08))"
        }
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          flexGrow: 1,
          pt: 1,
          pb: 0,
          "&:last-child": { pb: 0 },
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mr: "-12px" }}>
          <Typography variant="body2">Query Group {varGroup.id}</Typography>
          {!disabled && (
            <Stack direction="row" spacing={0} onClick={blockClicks}>
              <HelpBox title="Clear Query Group">
                <IconButton onClick={handleClear}>
                  <RestartAlt fontSize="small"/>
                </IconButton>
              </HelpBox>
              {numQueryGroups > 1 && (
                <HelpBox title="Remove Query Group">
                  <IconButton onClick={handleRemoveGroup}>
                    <Cancel fontSize="small"/>
                  </IconButton>
                </HelpBox>
              )}
            </Stack>
          )}
        </Box>

        <Box onClick={disabled ? undefined : blockClicks} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Match:
          </Typography>
          {operatorOptions.map((operator) => {
            const isSelected = varGroup.operator === operator.name;
            return (
              <HelpBox key={operator.name} title={operator.description}>
                <Chip
                  key={operator.name}
                  label={operator.label}
                  size="small"
                  variant={isSelected ? "filled" : "outlined"}
                  color={isSelected ? active ? "secondary" : "info" : "default"}
                  onClick={() => handleChangeOperator(operator.name as string)}
                  disabled={disabled || (allSameDemog && operator.name === 'AND')}
                />
              </HelpBox>
            )
          })}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <Typography variant="body2">
            Variables:
          </Typography>
          <Box
            sx={{
              border: 1,
              borderRadius: 1,
              borderColor: 'divider',
              minHeight: "48px",
              p: 1,
              flexGrow: 1,
              maxHeight: 300,
              overflowY: "auto"
            }}
          >
            {varGroup.variables.map((variable) => (
              <VariableChip
                variable={variable}
                color={active ? "secondary" : "info"}
                icon={getVariableIcon(variable)}
                onDelete={disabled ? undefined : (e) => {
                  e.stopPropagation();
                  handleVariableDelete(variable);
                }}
                disabled={disabled}
              />
            ))}
          </Box>
        </Box>
        {active ? (
          <Typography variant="overline" color="secondary" sx={{ textAlign: "center" }}>
            Active
          </Typography>
        ) : (
          <Box height={numQueryGroups > 1 ? 32 : 0} />
        )}

      </CardContent>
    </Card>
  );
};

export default SelectionCard;
